// assets/stz-complete-the-look.js
// "Complete the look" set upsell (blocks/stz-complete-the-look.liquid): checked rows with size
// buttons, a live set total (struck pre-discount total when the set discount is on), one Cart API
// call for every checked row, and the host row following the PDP's main variant picker.
// Same cart mechanics as product-form.js #processBatchAddToCart: POST {items, sections} to
// Theme.routes.cart_add_url and dispatch CartLinesUpdateEvent so the drawer opens and morphs.

import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * The set maths, pure so scripts/tests/theme-complete-the-look.test.mjs can pin it.
 * Lines: [{ price, side: 'top'|'bottom'|'', discountPair: boolean }] (checked, in-stock rows only).
 * The mirrored Buy X Get Y discounts each discount ONE item per qualifying item on the other side,
 * and Shopify allocates to the lowest-priced eligible items, so the total shown is the floor of
 * what the cart will do — never more.
 * @typedef {{ id: number, price: number, side: string, discountPair: boolean, productId?: string }} SetLine
 * @param {SetLine[]} lines
 * @param {{ enabled: boolean, percent: number }} discount
 * @returns {{ full: number, total: number, saved: number, discountedIds: number[] }}
 */
export function computeSetTotals(lines, { enabled, percent }) {
  const full = lines.reduce((sum, l) => sum + l.price, 0);
  if (!enabled || !(percent > 0)) return { full, total: full, saved: 0, discountedIds: [] };
  const byPrice = (a, b) => a.price - b.price;
  const tops = lines.filter((l) => l.discountPair && l.side === 'top').sort(byPrice);
  const bottoms = lines.filter((l) => l.discountPair && l.side === 'bottom').sort(byPrice);
  const pairs = Math.min(tops.length, bottoms.length);
  const discounted = [...tops.slice(0, pairs), ...bottoms.slice(0, pairs)];
  const discountedIds = discounted.map((l) => l.id);
  let total = 0;
  for (const line of lines) {
    total += discountedIds.includes(line.id) ? Math.round((line.price * (100 - percent)) / 100) : line.price;
  }
  return { full, total, saved: full - total, discountedIds };
}

class StzCompleteTheLook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.rows = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-stz-ctl-row]')));
    this.addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-stz-ctl-add]'));
    this.error = /** @type {HTMLElement | null} */ (this.querySelector('[data-stz-ctl-error]'));
    this.currency = this.dataset.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.discount = {
      enabled: this.dataset.discountEnabled === 'true',
      percent: Number(this.dataset.discountPercent) || 0,
    };

    this.addEventListener('change', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.matches('[data-stz-ctl-include]')) {
        const row = target.closest('[data-stz-ctl-row]');
        if (row) row.dataset.checked = String(/** @type {HTMLInputElement} */ (target).checked);
      }
      if (target.matches('input[type="radio"]')) this.#syncRowPrice(/** @type {HTMLElement | null} */ (target.closest('[data-stz-ctl-row]')));
      this.render();
    });
    this.addButton?.addEventListener('click', () => this.addSet());

    // The host row follows the PDP's main picker (colour and size).
    this.onProductSelect = (event) => this.#onProductSelect(event);
    document.addEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.loadComplements();
  }

  /**
   * Fetch the complement rows (sections/stz-complete-the-look-rows.liquid through the
   * recommendations route), adopt them under the host row, mark discount pairs against the host's
   * side, snap their colour to the host colourway, and only then show the block. No rows → stays hidden.
   */
  async loadComplements() {
    const url = this.dataset.recommendationsUrl;
    const list = this.querySelector('[data-stz-ctl-rows]');
    if (!url || !list) return;
    let rows = [];
    try {
      const html = await fetch(url, { headers: { Accept: 'text/html' } }).then((r) => (r.ok ? r.text() : ''));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      rows = /** @type {HTMLElement[]} */ (Array.from(doc.querySelectorAll('li[data-stz-ctl-row="complement"]')));
    } catch {
      rows = [];
    }
    const hostId = String(this.dataset.hostProductId);
    const seen = new Set();
    rows = rows.filter((row) => {
      const id = String(row.dataset.productId);
      if (id === hostId || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    if (rows.length === 0) {
      this.remove();
      return;
    }
    for (const row of rows) list.appendChild(document.adoptNode(row));
    this.rows = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-stz-ctl-row]')));
    this.#markPairs();
    const hostColor = this.dataset.hostColor || null;
    if (hostColor) {
      for (const row of this.rows) {
        if (row.dataset.stzCtlRow === 'host') continue;
        if (this.variantsOf(row).some((v) => v.color === hostColor && v.available)) this.renderSizes(row, hostColor);
      }
    }
    this.hidden = false;
    this.render();
  }

  /** A row earns the set discount when the setting is on and it pairs a set-top with a set-bottom. */
  #markPairs() {
    const hostSide = this.dataset.hostSide || '';
    const complements = this.rows.filter((r) => r.dataset.stzCtlRow !== 'host');
    const hasOpposite = hostSide !== '' && complements.some((r) => r.dataset.side && r.dataset.side !== hostSide);
    for (const row of this.rows) {
      const isHost = row.dataset.stzCtlRow === 'host';
      const pair = this.discount.enabled && hostSide !== '' && (isHost ? hasOpposite : Boolean(row.dataset.side) && row.dataset.side !== hostSide);
      row.dataset.discountPair = String(pair);
    }
    const sub = this.querySelector('[data-stz-ctl-sub]');
    const plain = this.querySelector('[data-stz-ctl-sub-plain]');
    const possible = this.discount.enabled && hasOpposite;
    if (sub) sub.hidden = !possible;
    if (plain) plain.hidden = possible;
  }

  disconnectedCallback() {
    if (this.onProductSelect) document.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  /**
   * @param {HTMLElement | null} row
   * @returns {Array<{ id: number, size: string | null, color: string | null, price: number, available: boolean }>}
   */
  variantsOf(row) {
    if (!row) return [];
    const cached = /** @type {any} */ (row).__variants;
    if (cached) return cached;
    let variants = [];
    try {
      variants = JSON.parse(row.querySelector('[data-stz-ctl-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }
    /** @type {any} */ (row).__variants = variants;
    return variants;
  }

  /**
   * The chosen line of a row, or null when unchecked / nothing selectable.
   * @param {HTMLElement} row
   * @returns {SetLine | null}
   */
  lineOf(row) {
    if (row.dataset.checked !== 'true') return null;
    const input = /** @type {HTMLInputElement | null} */ (
      row.querySelector('input[type="radio"]:checked') || row.querySelector('[data-stz-ctl-single]')
    );
    if (!input || input.dataset.available !== 'true') return null;
    return {
      id: Number(input.value),
      price: Number(input.dataset.price) || 0,
      side: row.dataset.side || '',
      discountPair: row.dataset.discountPair === 'true',
      productId: row.dataset.productId,
    };
  }

  /** @returns {SetLine[]} */
  lines() {
    return /** @type {SetLine[]} */ (this.rows.map((row) => this.lineOf(row)).filter(Boolean));
  }

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  render() {
    const lines = this.lines();
    const totals = computeSetTotals(lines, this.discount);
    const total = this.querySelector('[data-stz-ctl-total]');
    const full = this.querySelector('[data-stz-ctl-total-full]');
    if (total) total.textContent = this.#money(totals.total);
    if (full) {
      full.hidden = totals.saved <= 0;
      full.textContent = this.#money(totals.full);
    }
    if (this.addButton) this.addButton.disabled = lines.length === 0 || this.dataset.busy === 'true';
  }

  /** @param {HTMLElement | null} row */
  #syncRowPrice(row) {
    if (!row) return;
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[type="radio"]:checked'));
    const price = row.querySelector('[data-stz-ctl-price]');
    if (input && price) price.textContent = this.#money(Number(input.dataset.price) || 0);
  }

  /**
   * Re-render a row's size buttons for a colour, keeping (or choosing) the selected variant.
   * @param {HTMLElement} row
   * @param {string | null} color
   * @param {number} [selectedId]
   */
  renderSizes(row, color, selectedId) {
    const sizes = row.querySelector('[data-stz-ctl-sizes]');
    if (!sizes) return;
    const variants = this.variantsOf(row).filter((v) => color == null || v.color == null || v.color === color);
    const chosen = variants.find((v) => v.id === selectedId) || variants.find((v) => v.available) || variants[0];
    const name = /** @type {HTMLInputElement | null} */ (sizes.querySelector('input'))?.name || `stz-ctl-size-${row.dataset.productId}`;
    const legend = sizes.querySelector('legend')?.outerHTML || '';
    sizes.innerHTML =
      legend +
      variants
        .map(
          (v) => `<label class="stz-ctl__size"><input type="radio" name="${name}" value="${v.id}" data-price="${v.price}" data-available="${v.available}"${chosen && v.id === chosen.id ? ' checked' : ''}${v.available ? '' : ' disabled'} aria-label="${escapeHtml(v.size ?? '')}${v.available ? '' : ' - sold out'}"><span>${escapeHtml(v.size ?? '')}</span></label>`,
        )
        .join('');
    row.dataset.color = color ?? '';
    this.#syncRowPrice(row);
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  async #onProductSelect(event) {
    const host = this.rows.find((r) => r.dataset.stzCtlRow === 'host');
    if (!host || !event.promise) return;
    let detail;
    try {
      ({ detail } = await event.promise);
    } catch {
      return;
    }
    const resource = detail?.resource;
    if (!resource || String(detail?.productId ?? this.dataset.hostProductId) !== String(this.dataset.hostProductId)) return;
    const variant = this.variantsOf(host).find((v) => v.id === Number(resource.id));
    if (!variant) return;
    this.renderSizes(host, variant.color, variant.id);
    // Complement rows follow the host colourway when they carry it in stock.
    for (const row of this.rows) {
      if (row === host || variant.color == null) continue;
      const variants = this.variantsOf(row);
      if (variants.some((v) => v.color === variant.color && v.available)) {
        const current = Number(/** @type {HTMLInputElement | null} */ (row.querySelector('input[type="radio"]:checked'))?.value);
        const currentSize = variants.find((v) => v.id === current)?.size;
        const same = variants.find((v) => v.color === variant.color && v.size === currentSize && v.available);
        this.renderSizes(row, variant.color, same?.id);
      }
    }
    this.render();
  }

  /** @param {string} key */
  #setLabel(key) {
    const label = this.querySelector('[data-stz-ctl-add-label]');
    if (label) label.textContent = this.dataset[key] || label.textContent;
  }

  #refreshCart() {
    return fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  async addSet() {
    const lines = this.lines();
    if (lines.length === 0 || this.dataset.busy === 'true') return;
    this.dataset.busy = 'true';
    if (this.error) this.error.hidden = true;
    this.#setLabel('addingLabel');
    this.render();

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const payload = { items: lines.map((l) => ({ id: l.id, quantity: 1 })), sections: sectionIds.join(',') };
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: 1 })),
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch(this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());
      if (response.status) throw Object.assign(new Error(response.message || response.description || 'Add to cart failed'), { response });
      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'stz-complete-the-look', sourceId: this.id, itemCount: lines.length, productId: this.dataset.hostProductId, sections: response.sections, didError: false },
      });
      this.#setLabel('addedLabel');
      setTimeout(() => this.#setLabel('addLabel'), 2500);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: error?.response ? 'INVALID' : 'SERVICE_UNAVAILABLE' }));
      if (this.error) {
        this.error.textContent = error?.response?.message || this.dataset.errorText || 'Something went wrong.';
        this.error.hidden = false;
      }
      this.#setLabel('addLabel');
    } finally {
      this.dataset.busy = 'false';
      this.render();
    }
  }
}

/** @param {unknown} value */
function escapeHtml(value) {
  /** @type {Record<string, string>} */
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(value).replace(/[&<>"']/g, (c) => map[c] ?? c);
}

if (!customElements.get('stz-complete-the-look')) {
  customElements.define('stz-complete-the-look', StzCompleteTheLook);
}
